import { useEffect, useState } from "react";
import { Clock, Ticket } from "lucide-react";
import { whenText } from "@/components/Paywall";
import { useAuth } from "@/context/AuthContext";

// Access-window and pass time displays. Times count down even while the student is away.
const left = (iso, now) => {
  const m = Math.max(0, Math.round((new Date(iso).getTime() - now) / 60000));
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
};
const dur = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ""}` : `${min}m`);
const timeOf = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  return now;
}

export function passLine(p, now) {
  if (p.state === "not_started") return `Not started · ${dur(p.duration_min)} of unlimited messages`;
  if (p.state === "expired" || new Date(p.ends_at).getTime() <= now) return `Expired ${whenText(p.ends_at)}`;
  return `${left(p.ends_at, now)} left · ends ${whenText(p.ends_at)}`;
}

export function windowLine(ent, now) {
  const a = ent?.access;
  if (!a || ent.is_admin || ent.base_tier === "unlimited") return null;
  const locked = a.state === "locked" && new Date(a.available_at).getTime() > now;
  const active = a.state === "active" && new Date(a.window_end).getTime() > now;
  if (ent.base_tier === "free") return locked ? `Chat opens again ${whenText(a.available_at)}` : null;
  if (locked) return `Access ended · available again ${whenText(a.available_at)}`;
  if (active) return a.continuous ? `${left(a.window_end, now)} left in this window · renews with no wait` : `${left(a.window_end, now)} of access left`;
  return `${a.hours}-hour window ready · starts with your next message`;
}

// One short line for tight spaces (phone top bar, collapsed sidebar).
export function AccessChip({ className = "" }) {
  const { ent } = useAuth();
  const now = useNow();
  if (!ent || ent.is_admin) return null;
  const pass = (ent.passes || []).find((p) => p.state === "active" && new Date(p.ends_at).getTime() > now);
  const a = ent.access;
  let text = null;
  if (pass) text = `${pass.name} · ${left(pass.ends_at, now)}`;
  else if (a && ent.base_tier !== "unlimited") {
    if (a.state === "locked" && new Date(a.available_at).getTime() > now) text = `Opens ${timeOf(a.available_at)}`;
    else if (ent.base_tier !== "free" && a.state === "active" && new Date(a.window_end).getTime() > now) text = `${left(a.window_end, now)} left`;
  }
  if (!text) return null;
  return (
    <span title={pass ? passLine(pass, now) : windowLine(ent, now) || ""} data-testid="access-chip"
      className={`inline-flex items-center gap-1 text-[11px] text-neutral-300 rounded-full border border-white/10 px-2 py-0.5 ${className}`}>
      <Clock className="h-3 w-3 text-sky-300" />{text}
    </span>
  );
}

export function AccessIndicator() {
  const { ent } = useAuth();
  const now = useNow();
  if (!ent || ent.is_admin) return null;
  const line = windowLine(ent, now);
  const passes = ent.passes || [];
  if (!line && !passes.length) return null;
  return (
    <div className="mx-1 mb-1.5 rounded-lg bg-white/[0.03] px-2.5 py-2 space-y-1.5 text-[11px]" data-testid="access-indicator">
      {line && <p className="flex items-center gap-1.5 text-neutral-300" data-testid="access-window-line"><Clock className="h-3 w-3 text-sky-300 shrink-0" />{line}</p>}
      {passes.map((p) => (
        <p key={p.pass_id} className={`flex items-center gap-1.5 ${p.state === "expired" ? "text-neutral-500" : "text-yellow-200"}`} data-testid={`access-pass-${p.pass_id}`}>
          <Ticket className="h-3 w-3 shrink-0" /><span className="font-medium">{p.name}</span><span className="text-neutral-400">· {passLine(p, now)}</span>
        </p>
      ))}
    </div>
  );
}

// Billing page, once something is bought: how much messaging time is used and left in the current window and each pass.
const mins = (ms) => Math.max(0, Math.round(ms / 60000));

function windowUsage(a, now) {
  const total = a.hours * 60;
  if (a.state === "ready") return { used: 0, total, note: `Nothing used yet · ${dur(total)} left, starts with your next message` };
  const end = new Date(a.window_end).getTime();
  if (a.state === "active" && end > now) {
    const rest = mins(end - now);
    return { used: total - rest, total, note: `${dur(total - rest)} used · ${dur(rest)} left${a.continuous ? " · renews with no wait" : ""}` };
  }
  return { used: total, total, note: a.continuous ? "All used · renews with your next message" : `All used · more messages ${whenText(a.available_at || a.cycle_end)}` };
}

function passUsage(p, now) {
  const total = p.duration_min;
  if (p.state === "not_started") return { used: 0, total, note: `Nothing used yet · ${dur(total)} left, starts with your first message` };
  const end = new Date(p.ends_at).getTime();
  if (p.state === "expired" || end <= now) return { used: total, total, note: `All used · ended ${whenText(p.ends_at)}` };
  const rest = mins(end - now);
  return { used: total - rest, total, note: `${dur(total - rest)} used · ${dur(rest)} left · ends ${timeOf(p.ends_at)}` };
}

function Meter({ label, used, total, note, testid, tone }) {
  const pct = total ? Math.min(100, Math.round((100 * used) / total)) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex justify-between gap-3 text-xs"><span className="text-neutral-300 shrink-0">{label}</span>
        <span className="text-neutral-500 text-right" data-testid={`${testid}-text`}>{note}</span></div>
      <div className="h-1 rounded-full bg-white/5 mt-2 overflow-hidden"><div className={`h-full ${tone} transition-[width] duration-500`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function AccessDetails({ ent }) {
  const now = useNow();
  const a = ent.access;
  const sub = ent.subscription;
  const w = sub && a && ent.base_tier !== "unlimited" ? windowUsage(a, now) : null;
  const passes = ent.passes || [];
  return (
    <div className="space-y-4" data-testid="access-details">
      {sub && ent.base_tier === "unlimited" && <p className="text-sm text-neutral-300" data-testid="access-details-unlimited">Unlimited messages · nothing to count down</p>}
      {w && <Meter label="Messaging time" testid="access-details-window" tone="bg-sky-400" {...w} />}
      {passes.map((p) => <Meter key={p.pass_id} label={p.name} testid={`access-details-pass-${p.pass_id}`} tone="bg-yellow-300" {...passUsage(p, now)} />)}
      {(w || passes.length > 0) && <p className="text-[11px] text-neutral-500">Time keeps counting while you're away; it isn't the same as time actively studying.</p>}
    </div>
  );
}
